import React, { useEffect, useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetScheduleLogs } from '~/hooks/apis/offers/useGetScheduleLogs';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';

import { formatDateLongMonthWithMeridiemNoSeconds as formatDate } from '~/services/TimeService';

import { CampaignScheduleDropdownRow } from './CampaignScheduleExpandableRow';
import { buildCampaignScheduleHistoryEntries } from './campaignScheduleHistoryDiff';
import { formatDiffValue, getActionLabel, getDiffFieldLabel, getScheduleLabel } from './campaignScheduleHistoryDisplay';
import { handleExpandRowKeyDown, toggleExpandedRow } from './campaignScheduleTabLayout';

// Only employees edit schedules, and svc-auth's employee/admin lookups aren't tenant-siloed per brand
// (unlike customer accounts) — a single brand here is safe regardless of which brand's schedule changed.
const DEFAULT_BRAND = 'luxuryescapes';
const PAGE_SIZE = 20;
const HISTORY_TABLE_COLUMN_COUNT = 4;

const ACTION_CHIP_COLOR: Record<string, 'success' | 'info' | 'error'> = {
  INSERT: 'success',
  UPDATE: 'info',
  DELETE: 'error',
};

interface Props {
  offerId?: string;
}

export default function CampaignScheduleHistoryCard({ offerId }: Props) {
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const {
    data: logs = [],
    isLoading: isLoadingLogs,
    error: logsError,
  } = useGetScheduleLogs(offerId, { enabled: !!offerId });

  const allEntries = useMemo(() => buildCampaignScheduleHistoryEntries(logs), [logs]);
  const totalPages = Math.max(Math.ceil(allEntries.length / PAGE_SIZE), 1);

  // Reset to page 1 when switching offers, or when the entry count changes (e.g. after an import) —
  // otherwise a stale `page` past the new totalPages would slice to an empty page.
  useEffect(() => {
    setPage(1);
  }, [offerId, allEntries.length]);

  const currentPage = Math.min(page, totalPages);
  const entries = useMemo(
    () => allEntries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [allEntries, currentPage],
  );

  const userIds = useMemo(() => entries.map((entry) => entry.memberId), [entries]);
  const { data: usersById = new Map(), isLoading: isLoadingUsers } = useGetUsersSummaryByIds({
    userIds,
    brand: DEFAULT_BRAND,
  });

  // Only the logs fetch blocks the whole card — usersById's query key changes every page, so gating on
  // it too would flash a spinner on each navigation instead of showing "Loading…" per name.
  const isLoading = isLoadingLogs;

  return (
    <Paper variant="outlined">
      <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Typography fontWeight={700} variant="h3">
          Schedule History
        </Typography>
        <Chip
          label={`${allEntries.length} change${allEntries.length === 1 ? '' : 's'}`}
          size="small"
          variant="outlined"
        />
      </Stack>

      {isLoading && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={32} />
        </Stack>
      )}

      {!isLoading && logsError && (
        <Box sx={{ px: 3, pb: 3 }}>
          <Alert severity="error">Unable to load schedule history.</Alert>
        </Box>
      )}

      {!isLoading && !logsError && entries.length === 0 && (
        <Typography align="center" color="text.secondary" sx={{ pb: 3 }} variant="body2">
          No schedule changes yet.
        </Typography>
      )}

      {!isLoading && !logsError && entries.length > 0 && (
        <>
          <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
            <Table size="small" sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: 'background.paper',
                      color: 'text.secondary',
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0,
                      textTransform: 'uppercase',
                    },
                  }}
                >
                  <TableCell />
                  <TableCell>Schedule</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Changed by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const user = usersById.get(entry.memberId);
                  const [primarySchedule, ...otherSchedules] = entry.schedules;
                  const hasMoreSchedules = otherSchedules.length > 0;
                  const isExpanded = expandedRows.has(entry.id);

                  return (
                    <React.Fragment key={entry.id}>
                      <TableRow
                        hover={hasMoreSchedules}
                        role={hasMoreSchedules ? 'button' : undefined}
                        sx={{ cursor: hasMoreSchedules ? 'pointer' : 'default' }}
                        tabIndex={hasMoreSchedules ? 0 : undefined}
                        onClick={
                          hasMoreSchedules
                            ? () => setExpandedRows((current) => toggleExpandedRow(current, entry.id))
                            : undefined
                        }
                        onKeyDown={
                          hasMoreSchedules
                            ? (event) =>
                                handleExpandRowKeyDown(event, () =>
                                  setExpandedRows((current) => toggleExpandedRow(current, entry.id)),
                                )
                            : undefined
                        }
                      >
                        <TableCell sx={{ width: 32 }}>
                          {hasMoreSchedules &&
                            (isExpanded ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            ))}
                        </TableCell>
                        <TableCell>
                          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={1}>
                            <Chip
                              color={ACTION_CHIP_COLOR[entry.action]}
                              label={getActionLabel(entry.action)}
                              size="small"
                              variant="outlined"
                            />
                            <Typography fontWeight={700} variant="body2">
                              {getScheduleLabel(primarySchedule)}
                            </Typography>
                            {hasMoreSchedules && (
                              <Chip label={`+${otherSchedules.length} more`} size="small" variant="outlined" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {entry.diffs.length > 0 ? (
                            <Stack spacing={0.25}>
                              {entry.diffs.map((diff) => (
                                <Typography key={diff.field} variant="body2">
                                  <Typography color="text.secondary" component="span" variant="body2">
                                    {getDiffFieldLabel(diff.field)}:{' '}
                                  </Typography>
                                  {formatDiffValue(diff, diff.before)}
                                  {' → '}
                                  <Typography component="span" fontWeight={700} variant="body2">
                                    {formatDiffValue(diff, diff.after)}
                                  </Typography>
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              No further details
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0}>
                            <Typography variant="body2">
                              {isLoadingUsers ? 'Loading…' : (user?.full_name ?? 'Unknown user')}
                            </Typography>
                            <Typography color="text.secondary" variant="caption">
                              {formatDate(entry.loggedAt)}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {hasMoreSchedules && (
                        <CampaignScheduleDropdownRow colSpan={HISTORY_TABLE_COLUMN_COUNT} isExpanded={isExpanded}>
                          <Typography color="text.secondary" variant="caption">
                            Applied to {entry.schedules.length} schedules in this change:
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {entry.schedules.map((schedule) => (
                              <Chip
                                key={getScheduleLabel(schedule)}
                                label={getScheduleLabel(schedule)}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        </CampaignScheduleDropdownRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <Pagination
                color="primary"
                count={totalPages}
                page={currentPage}
                onChange={(_event, value) => setPage(value)}
              />
            </Stack>
          )}
        </>
      )}
    </Paper>
  );
}
